import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const Page = styled.div`min-height:100vh;background:#0a0a0f;color:#e2e8f0;font-family:'Inter','Pretendard',sans-serif;padding:20px;`;
const TopBar = styled.div`display:flex;align-items:center;gap:12px;margin-bottom:28px;padding-bottom:20px;border-bottom:1px solid #1e2030;flex-wrap:wrap;`;
const BackBtn = styled.button`background:none;border:none;color:#64748b;cursor:pointer;padding:8px;border-radius:8px;&:hover{background:#1e2030;color:#e2e8f0}`;
const TitleArea = styled.div`flex:1;min-width:200px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:800;margin:0;background:linear-gradient(135deg,#8b5cf6,#f59e0b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;`;
const PageSub = styled.p`margin:2px 0 0;font-size:12px;color:#64748b;`;
const NavTabs = styled.div`display:flex;gap:8px;flex-wrap:wrap;`;
const NavTab = styled.button`
  background:${p => p.active ? 'rgba(139,92,246,0.15)' : 'transparent'};
  border:1px solid ${p => p.active ? '#8b5cf6' : '#1e2030'};
  color:${p => p.active ? '#8b5cf6' : '#64748b'};
  padding:7px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;transition:all .2s;
  &:hover{border-color:#8b5cf6;color:#8b5cf6}
`;

const Card = styled.div`background:#111827;border:1px solid #1e2030;border-radius:14px;padding:20px;margin-bottom:16px;animation:${fadeIn} .4s ease;`;
const PnlBadge = styled.span`
  font-size:13px;font-weight:700;padding:4px 10px;border-radius:8px;
  background:${p => p.positive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)'};
  color:${p => p.positive ? '#10b981' : '#ef4444'};
`;
const InfoRow = styled.div`display:flex;gap:16px;flex-wrap:wrap;font-size:12px;color:#94a3b8;margin-top:10px;line-height:1.8;`;
const InfoTag = styled.span`background:#1e2030;padding:3px 8px;border-radius:6px;font-size:11px;`;
const TabBar = styled.div`display:flex;gap:8px;margin-bottom:20px;`;
const Tab = styled.button`
  padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;border:none;
  background:${p => p.active ? 'rgba(139,92,246,0.15)' : '#1e2030'};
  color:${p => p.active ? '#8b5cf6' : '#64748b'};
  &:hover{color:#8b5cf6}
`;

function MinerviniPositionsPage() {
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open');

  useEffect(() => {
    (async () => {
      try {
        const [open, all] = await Promise.all([
          apiService.getMinerviniPositions(),
          apiService.getMinerviniAllPositions(),
        ]);
        setPositions(open); setAllPositions(all);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const fmt = n => n != null ? Number(n).toLocaleString() : '-';
  const pct = n => n != null ? `${n > 0 ? '+' : ''}${Number(n).toFixed(2)}%` : '-';
  const list = tab === 'open' ? positions : allPositions;

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/minervini')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>📐 포지션 현황</PageTitle>
          <PageSub>현재 보유 포지션 및 VCP 상세 정보</PageSub>
        </TitleArea>
        <NavTabs>
          <NavTab onClick={() => navigate('/labs/minervini')}>대시보드</NavTab>
          <NavTab active onClick={() => navigate('/labs/minervini/positions')}>포지션</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/journal')}>거래일지</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/screener')}>스크리너</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/settings')}><Settings size={13} /></NavTab>
        </NavTabs>
      </TopBar>

      <TabBar>
        <Tab active={tab === 'open'} onClick={() => setTab('open')}>🟢 보유중 ({positions.length})</Tab>
        <Tab active={tab === 'all'} onClick={() => setTab('all')}>📋 전체 이력 ({allPositions.length})</Tab>
      </TabBar>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#475569' }}>로딩 중...</div>
      ) : list.length === 0 ? (
        <Card style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>포지션이 없습니다.</Card>
      ) : (
        list.map(pos => (
          <Card key={pos.symbol || pos.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: 16 }}>{pos.name}</span>
                <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>{pos.symbol}</span>
                {pos.status === 'CLOSED' && <InfoTag style={{ marginLeft: 8, color: '#ef4444' }}>CLOSED</InfoTag>}
                {pos.status === 'RESET' && <InfoTag style={{ marginLeft: 8, color: '#f59e0b' }}>RESET</InfoTag>}
              </div>
              {tab === 'open' && pos.unrealizedRate != null && (
                <PnlBadge positive={pos.unrealizedRate >= 0}>{pct(pos.unrealizedRate)}</PnlBadge>
              )}
              {tab !== 'open' && pos.pnlRate != null && (
                <PnlBadge positive={pos.pnlRate >= 0}>{pct(pos.pnlRate)}</PnlBadge>
              )}
            </div>
            <InfoRow>
              <span>진입가: <b style={{ color: '#e2e8f0' }}>{fmt(pos.entryPrice)}원</b></span>
              {tab === 'open' && <span>현재가: <b style={{ color: '#e2e8f0' }}>{fmt(pos.currentPrice)}원</b></span>}
              {pos.exitPrice && <span>청산가: <b style={{ color: '#e2e8f0' }}>{fmt(pos.exitPrice)}원</b></span>}
              <span>수량: <b>{pos.quantity}주</b></span>
              <span>손절선: <b style={{ color: '#ef4444' }}>{fmt(pos.stopLoss)}원</b> ({pos.stopLossPercent}%)</span>
            </InfoRow>
            <InfoRow>
              <span>피벗: <b style={{ color: '#f59e0b' }}>{fmt(pos.pivotPoint)}원</b></span>
              <span>TT 점수: <b style={{ color: '#8b5cf6' }}>{pos.trendTemplateScore}점</b></span>
              <span>RS: <b>{pos.relativeStrength}</b></span>
              {pos.fundamentalBonus > 0 && <span>펀더멘털: <b style={{ color: '#10b981' }}>+{pos.fundamentalBonus}점</b></span>}
            </InfoRow>
            {pos.vcpDetails && (
              <InfoRow style={{ marginTop: 6 }}>
                <InfoTag style={{ color: '#06b6d4' }}>VCP {pos.vcpDetails.tCount}T</InfoTag>
                {pos.vcpDetails.tightness && <InfoTag>타이트니스: {pos.vcpDetails.tightness}%</InfoTag>}
                {pos.vcpDetails.breakoutVolumeRatio > 0 && (
                  <InfoTag>돌파 거래량: {pos.vcpDetails.breakoutVolumeRatio}x</InfoTag>
                )}
                {pos.vcpDetails.contractions?.map((c, i) => (
                  <InfoTag key={i} style={{ color: '#94a3b8' }}>
                    {i + 1}차: -{c.depth}% ({c.duration}일)
                  </InfoTag>
                ))}
              </InfoRow>
            )}
            <div style={{ fontSize: 11, color: '#475569', marginTop: 8 }}>
              {pos.entryDate && <span>진입: {pos.entryDate}</span>}
              {pos.exitDate && <span style={{ marginLeft: 12 }}>청산: {pos.exitDate}</span>}
              {pos.closedReason && <span style={{ marginLeft: 12, color: '#ef4444' }}>사유: {pos.closedReason}</span>}
            </div>
          </Card>
        ))
      )}
    </Page>
  );
}

export default MinerviniPositionsPage;
